import {Box, createStyles, Table, Text} from '@mantine/core';
import React from 'react';

import {AntibodyEnforcedSchemaFieldSpec} from 'antibodies/schema-configuration/AntibodyEnforcedDataModelConstants';
import {SchemaFieldSelectorsForMigrationQuery_schemaFieldSelectors_SchemaFieldSelectorNode as RegistrySchemaFieldSelector} from 'antibodies/schema-configuration/schema-migration/__generated/SchemaFieldSelectorsForMigrationQuery';
import {FieldMigrationMapping} from 'antibodies/schema-configuration/schema-migration/AntibodySchemaMigrationConstants';
import NewFieldMigrationRow from 'antibodies/schema-configuration/schema-migration/NewFieldMigrationRow';
import {
  areFieldTypesCompatibleForMigration,
  collateCompatibleDropdowns,
  getDestinationOptionNames,
  getSourceOptionsForField,
  hasUnmappedSourceOptions,
  MIGRATABLE_FIELD_TYPES,
  speccedDropdownToSchemaFieldSelector,
} from 'antibodies/schema-configuration/schema-migration/newFieldsMigrationUtils';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';

import MigrationPreviewSection from './MigrationPreviewSection';

export interface NewFieldsMigrationPreviewProps {
  fieldsToCreate: Array<ClientSchemaField>;
  enforcedFieldSpecs: Array<AntibodyEnforcedSchemaFieldSpec>;
  existingFields: Array<SchemaField | ClientSchemaField>;
  fieldMigrationMappings: Array<FieldMigrationMapping>;
  onMappingsChange: React.Dispatch<React.SetStateAction<Array<FieldMigrationMapping>>>;
  allRegistrySelectors: Array<RegistrySchemaFieldSelector>;
  onOpenOptionMapping: (destFieldName: string) => void;
}

// Gives the Mantine Table a bordered, gray-header look consistent with other settings tables.
export const useStyles = createStyles((theme) => ({
  tableWrapper: {
    border: `1px solid ${theme.colors.gray[2]}`,
    borderRadius: theme.radius.sm,
    overflow: 'hidden',
  },
  table: {
    // Fixed layout so column widths are driven by these header widths, not by cell content — this
    // stops every column from nudging when the source Select renders.
    tableLayout: 'fixed',
    width: '100%',
    '& thead tr th': {
      backgroundColor: theme.colors.gray[0],
      borderBottom: `1px solid ${theme.colors.gray[2]}`,
      color: theme.colors.gray[7],
      fontWeight: 600,
      whiteSpace: 'nowrap',
    },
    '& thead tr th:nth-of-type(1)': {width: '28%'},
    '& thead tr th:nth-of-type(2)': {width: '24%'},
    '& thead tr th:nth-of-type(3)': {width: '16%'},
    '& thead tr th:nth-of-type(4)': {width: '32%'},
    '& thead tr th:first-of-type': {
      color: theme.colors.gray[9],
    },
    '& tbody tr td': {
      borderTop: `1px solid ${theme.colors.gray[2]}`,
      // Center every cell vertically so text-only rows and rows containing a 42px md Select share the
      // same baseline; consistent vertical padding gives all rows an even rhythm regardless of content.
      verticalAlign: 'middle',
      paddingTop: theme.spacing.sm,
      paddingBottom: theme.spacing.sm,
    },
    '& tbody tr:first-of-type td': {
      borderTop: 'none',
    },
  },
  iconStyle: {
    color: theme.colors.gray[7],
    flexShrink: 0,
    height: '12px',
    width: '12px',
  },
  // Signals a source option that has not been mapped or explicitly dropped yet — needs human review.
  warningIcon: {
    color: theme.colors.orange[6],
    flexShrink: 0,
    height: '12px',
    width: '12px',
  },
}));

export default function NewFieldsMigrationPreview({
  fieldsToCreate,
  enforcedFieldSpecs,
  existingFields,
  fieldMigrationMappings,
  onMappingsChange,
  allRegistrySelectors,
  onOpenOptionMapping,
}: NewFieldsMigrationPreviewProps): JSX.Element {
  const {classes} = useStyles();

  const schemaFieldSelectors = collateCompatibleDropdowns(enforcedFieldSpecs).map((dropdown) =>
    speccedDropdownToSchemaFieldSelector(dropdown)
  );
  if (fieldsToCreate.length === 0) {
    return (
      <MigrationPreviewSection title="New fields">
        <Text size="sm" color="dimmed">
          No new fields will be added.
        </Text>
      </MigrationPreviewSection>
    );
  }

  function getSourceOptions(destFieldName: string): Array<{id: string; name: string}> | null {
    return getSourceOptionsForField(
      destFieldName,
      fieldMigrationMappings,
      existingFields,
      allRegistrySelectors
    );
  }

  function handleOpenOptionMapping(destFieldName: string, allowedOptions: Array<string>): void {
    const existing = fieldMigrationMappings.find((m) => m.destinationFieldName === destFieldName);
    if (
      existing != null &&
      (existing.optionValueMappings == null || existing.optionValueMappings.length === 0)
    ) {
      const sourceOptions = getSourceOptions(destFieldName);
      if (sourceOptions != null) {
        const initial = sourceOptions
          .map((sourceOpt) => {
            const match = allowedOptions.find((name) => name.toLowerCase() === sourceOpt.name.toLowerCase());
            return match != null ? {sourceOptionId: sourceOpt.id, destinationOptionName: match} : null;
          })
          .filter((m): m is NonNullable<typeof m> => m != null);
        if (initial.length > 0) {
          onMappingsChange((prev) =>
            prev.map((m) =>
              m.destinationFieldName === destFieldName ? {...m, optionValueMappings: initial} : m
            )
          );
        }
      }
    }
    onOpenOptionMapping(destFieldName);
  }

  function handleSourceFieldChange(destFieldName: string, sourceFieldId: string | null): void {
    onMappingsChange((prev) => {
      const without = prev.filter((m) => m.destinationFieldName !== destFieldName);
      return sourceFieldId != null
        ? [...without, {sourceFieldId, destinationFieldName: destFieldName}]
        : without;
    });
  }

  // Only show the "Source field" column when there is something to map: at least one migratable
  // destination field type AND at least one existing field of a compatible type to migrate from.
  const showSourceColumn = fieldsToCreate.some(
    (df) =>
      MIGRATABLE_FIELD_TYPES.has(df.fieldType) &&
      existingFields.some(
        (f) =>
          areFieldTypesCompatibleForMigration(f.fieldType, df.fieldType) &&
          f.apiId != null &&
          f.archiveRecord == null
      )
  );

  return (
    <MigrationPreviewSection title="New fields">
      <Box className={classes.tableWrapper}>
        <Table className={classes.table}>
          <thead>
            <tr>
              <th>Field name</th>
              <th>Definition</th>
              <th>Validation</th>
              {showSourceColumn && <th>Source field</th>}
            </tr>
          </thead>
          <tbody>
            {fieldsToCreate.map((destField) => {
              const spec = enforcedFieldSpecs.find((s) => s.fieldName === destField.name);
              const allowedOptions = getDestinationOptionNames(spec, destField.schemaFieldSelectorId);
              const isMigratable = MIGRATABLE_FIELD_TYPES.has(destField.fieldType);
              const currentMapping = fieldMigrationMappings.find(
                (m) => m.destinationFieldName === destField.name
              );
              const sourceFieldId = currentMapping?.sourceFieldId ?? null;
              const sourceOptions = isMigratable ? getSourceOptions(destField.name) : null;
              // Only flag once a source field is chosen — before that there are no options to map.
              const hasUnmapped =
                sourceFieldId != null &&
                allowedOptions.length > 0 &&
                hasUnmappedSourceOptions(sourceOptions, currentMapping?.optionValueMappings, allowedOptions);

              const matchingFields = isMigratable
                ? existingFields.filter(
                    (f) =>
                      areFieldTypesCompatibleForMigration(f.fieldType, destField.fieldType) &&
                      f.apiId != null &&
                      f.archiveRecord == null
                  )
                : [];

              return (
                <NewFieldMigrationRow
                  key={destField.name}
                  destField={destField}
                  allowedOptions={allowedOptions}
                  isMigratable={isMigratable}
                  currentMapping={currentMapping}
                  matchingFields={matchingFields}
                  hasUnmapped={hasUnmapped}
                  showSourceColumn={showSourceColumn}
                  schemaFieldSelectors={schemaFieldSelectors}
                  classes={classes}
                  onSourceFieldChange={(val) => {
                    handleSourceFieldChange(destField.name, val);
                  }}
                  onOpenOptionMapping={() => {
                    handleOpenOptionMapping(destField.name, allowedOptions);
                  }}
                />
              );
            })}
          </tbody>
        </Table>
      </Box>
    </MigrationPreviewSection>
  );
}
